import * as Cesium from "cesium";


/**
 * validationConstants.ts
 * 
 * This file contains global validation rules used across the application.
 * 
 * 🚀 **Usage:** Instead of defining validation constraints separately in components,
 *    always import and use these predefined constants.
 * 
 * ✅ **Example:**
 *    import { VALIDATION_RULES } from '../utils/validationConstants';
 *    
 *    const altitude = 500; // Example altitude input
 *    
 *    if (altitude < VALIDATION_RULES.ORBITAL_ALTITUDE.MIN || altitude > VALIDATION_RULES.ORBITAL_ALTITUDE.MAX) {
 *        console.log("Invalid altitude! It should be between 300 km and 2000 km.");
 *    }
 * 
 * 📌 **Why use this?**
 *    - Ensures consistency across all validations.
 *    - Centralized updates: Modify once, apply everywhere.
 *    - Improves code maintainability and readability.
 * 
 * 📢 **Developers Note:** Always use these constants instead of hardcoding values in components.
 */

//Constant color array for saellite orbits
export const DEFAULT_ORBIT_COLOR = [240, 240, 240, 1]
export const COLORS = ["[233, 30, 99, 1]", "[170, 37, 193, 1]", "[112, 59, 205, 1]", "[67, 87, 196, 1]", "[34, 121, 190, 1]", "[0, 142, 161, 1]", "[0, 150, 136, 1]", "[61, 153, 65, 1]", "[134, 145, 24, 1]"];

export const VALIDATION_RULES = {
    MIN_FOV: 1,
    MAX_FOV: 180,
    MIN_AGILITY: 1,
    MAX_AGILITY: 180,
    MIN_SWATH: 1,
    MAX_SWATH: 1000,
    MIN_SAT_COUNT: 1,
    MAX_SAT_COUNT: 125,
    SAT_INPUT_LENGTH: 3,
    MIN_PLANE_COUNT: 1,
    MAX_PLANE_COUNT: 125,
    PLANE_INPUT_LENGTH: 3,
    EARTH_RADIUS: 6378.135, // Radius of the Earth in km
    GROUND_STATION_LATITUDE: {
        MIN: -90, // Minimum latitude for ground stations (in degrees)
        MAX: 90,  // Maximum latitude for ground stations (in degrees)
        INPUT_LENGTH: 8,
        PARAMETER: '°'
    },
    GROUND_STATION_LONGITUDE: {
        MIN: -180, // Minimum longitude for ground stations (in degrees)
        MAX: 180,  // Maximum longitude for ground stations (in degrees)
        INPUT_LENGTH: 8,
        PARAMETER: '°'
    },
    GROUND_STATION_ALTITUDE: {
        MIN: 0, // Minimum altitude for ground stations (in m)
        MAX: 9000, // Maximum altitude for ground stations (in m)
        INPUT_LENGTH: 9,
        PARAMETER: 'm'
    },
    GROUND_STATION_GT: {
        MIN: 0, // Minimum GT for ground stations (in km)
        MAX: 1000, // Maximum GT for ground stations (in km)
        INPUT_LENGTH: 8,
    },
    GROUND_STATION_UHF_FREQ: {
        MIN: 300, // Minimum UHF RX frequency for ground stations (in MHz)
        MAX: 3000, // Maximum UHF RX frequency for ground stations (in MHz)
        INPUT_LENGTH: 8,
    },
    GROUND_STATION_S_BAND_FREQ: {
        MIN: 2000, // Minimum S Band RX frequency for ground stations (in MHz)
        MAX: 4000, // Maximum S Band RX frequency for ground stations (in MHz)
        INPUT_LENGTH: 8,
    },
    GROUND_STATION_KA_BAND_FREQ: {
        MIN: 25500, // Minimum S Band RX frequency for ground stations (in MHz)
        MAX: 27000, // Maximum S Band RX frequency for ground stations (in MHz)
        INPUT_LENGTH: 8,
    },
    GROUND_STATION_X_BAND_FREQ: {
        MIN: 7800, // Minimum X Band RX frequency for ground stations (in MHz)
        MAX: 8500, // Maximum X Band RX frequency for ground stations (in MHz)
        INPUT_LENGTH: 8,
    },

    ORBITAL_ALTITUDE: {
        MIN: 300,  // Minimum altitude for orbital parameters (in km)
        MAX: 15000, // Maximum altitude for orbital parameters (in km)
        DEFAULT: 450,
        INPUT_LENGTH: 13,
        PARAMETER: 'km'
    },
    ORBITAL_INCLINATION: {
        MIN: 0,    // Minimum inclination for orbital parameters (in degrees)
        MAX: 180,  // Maximum inclination for orbital parameters (in degrees)
        DEFAULT: 37,
        INPUT_LENGTH: 12,
        PARAMETER: '°'
    },
    ORBITAL_ECCENTRICITY: {
        MIN: 0, // Minimum eccentricity for orbital parameters
        MAX: 0.05, // Maximum eccentricity for orbital parameters
        DEFAULT: 0,
        INPUT_LENGTH: 10,
    },
    ORBITAL_AP: {
        MIN: 0, // Minimum argument of perigee for orbital parameters (in degrees)
        MAX: 360, // Maximum argument of perigee for orbital parameters (in degrees)
        DEFAULT: 0,
        INPUT_LENGTH: 12,
        PARAMETER: '°'
    },
    ORBITAL_RAAN: {
        MIN: 0, // Minimum raan for orbital parameters (in degrees)
        MAX: 360, // Maximum raan for orbital parameters (in degrees)
        DEFAULT: 0,
        INPUT_LENGTH: 12,
        PARAMETER: '°'
    },
    ORBITAL_TA: {
        MIN: 0, // Minimum true anomaly for orbital parameters (in degrees)
        MAX: 360, // Maximum true anomaly for orbital parameters (in degrees)
        DEFAULT: 0,
        INPUT_LENGTH: 12,
        PARAMETER: '°'
    },
    ORBITAL_SEM: {
        MIN: 6671, // Minimum semimajor axis for orbital parameters (in degrees)
        MAX: 7121, // Maximum semimajor axis for orbital parameters (in degrees)
        DEFAULT: 6671,
        INPUT_LENGTH: 13,
        PARAMETER: 'km'
    },
    INTERNAL_PLANE_PHASING: {
        MIN: 0.1, // Minimum internal plane phasing for orbital parameters (in degrees)
        MAX: 360, // Maximum internal plane phasing for orbital parameters (in degrees)
        DEFAULT: 0.1,
        INPUT_LENGTH: 9,
        PARAMETER: '°'
    },
    PLANE_SEPARATION_ANGLE: {
        MIN: 0.1, // Minimum plane separation angle for orbital parameters (in degrees)
        DEFAULT: 0.1,
        MAX: 360, // Maximum plane separation angle for orbital parameters (in degrees)
        INPUT_LENGTH: 9,
        PARAMETER: '°'
    },
    SATELLITE_COUNT: {
        MIN: 1,
        MAX: 50,
        PARAMETER: ''
    },
    // SENSOR PLACEMENT VALIDATIONS
    ANGLE_RANGE: {
        MIN: 0,
        MAX: 360
    },

    ORIENTATION_RANGE: {
        MIN: -1,
        MAX: 1
    }


} as const;

export const WORLD_MAP_STYLE = {
    "version": 8,
    "projection": {
        "name": "equirectangular"
    },
    "sources": {
        "countries": {
            "type": "vector",
            "tiles": [`${process.env.PUBLIC_URL}/countries/{z}/{x}/{y}.pbf`],
            "maxzoom": 1
        }
    },
    "glyphs": `${process.env.PUBLIC_URL}/font/{fontstack}/{range}.pbf`,
    "layers":
        [
            {
                "id": "background",
                "type": "background",
                "paint": {
                    "background-color": "#1a1a1a"
                }
            },
            {
                "id": "area-white-1",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'ATA-1'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-white-2",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'ATA-2'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-red",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'AFG', 'ALD', 'BEN', 'BLR', 'BWA', 'COK', 'COL', 'DNK', 'DOM', 'ERI', 'FIN', 'FRA', 'FRO', 'GIB', 'GNB', 'GNQ', 'GRC', 'GTM', 'JPN', 'KIR', 'LKA', 'MHL', 'MMR', 'MWI', 'NCL', 'OMN', 'RWA', 'SMR', 'SVK', 'SYR', 'TCD', 'TON', 'URY', 'WLF'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-orange",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'AZE', 'BGD', 'CHL', 'CMR', 'CSI', 'DEU', 'DJI', 'GUY', 'HUN', 'IOA', 'JAM', 'LBN', 'LBY', 'LSO', 'MDG', 'MKD', 'MNG', 'MRT', 'NIU', 'NZL', 'PCN', 'PYF', 'SAU', 'SHN', 'STP', 'TTO', 'UGA', 'UZB', 'ZMB'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-yellow",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'AGO', 'ASM', 'ATF', 'BDI', 'BFA', 'BGR', 'BLZ', 'BRA', 'CHN', 'CRI', 'ESP', 'HKG', 'HRV', 'IDN', 'IRN', 'ISR', 'KNA', 'LBR', 'LCA', 'MAC', 'MUS', 'NOR', 'PLW', 'POL', 'PRI', 'SDN', 'TUN', 'UMI', 'USA', 'USG', 'VIR', 'VUT'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-green",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'ARE', 'ARG', 'BHS', 'CIV', 'CLP', 'DMA', 'ETH', 'GAB', 'GRD', 'GRL', 'HMD', 'IND', 'IOT', 'IRL', 'IRQ', 'ITA', 'KOS', 'LUX', 'MEX', 'NAM', 'NER', 'PHL', 'PRT', 'RUS', 'SEN', 'SUR', 'TZA', 'VAT'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-turquoise",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'AUT', 'BEL', 'BHR', 'BMU', 'BRB', 'CYN', 'DZA', 'EST', 'FLK', 'GMB', 'GUM', 'HND', 'JEY', 'KGZ', 'LIE', 'MAF', 'MDA', 'NGA', 'NRU', 'SLB', 'SOL', 'SRB', 'SWZ', 'THA', 'TUR', 'VEN', 'VGB'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-blue",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'AIA', 'BIH', 'BLM', 'BRN', 'CAF', 'CHE', 'COM', 'CPV', 'CUB', 'ECU', 'ESB', 'FSM', 'GAZ', 'GBR', 'GEO', 'KEN', 'LTU', 'MAR', 'MCO', 'MDV', 'NFK', 'NPL', 'PNG', 'PRY', 'QAT', 'SLE', 'SPM', 'SYC', 'TCA', 'TKM', 'TLS', 'VNM', 'WEB', 'WSB', 'YEM', 'ZWE'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-purple",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'ABW', 'ALB', 'AND', 'ATC', 'BOL', 'COD', 'CUW', 'CYM', 'CYP', 'EGY', 'FJI', 'GGY', 'IMN', 'KAB', 'KAZ', 'KWT', 'LAO', 'MLI', 'MNP', 'MSR', 'MYS', 'NIC', 'NLD', 'PAK', 'PAN', 'PRK', 'ROU', 'SGS', 'SVN', 'SWE', 'TGO', 'TWN', 'VCT', 'ZAF'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
            },
            {
                "id": "area-pink",
                "type": "fill",
                "source": "countries",
                "filter": ["in", "ADM0_A3", 'ARM', 'ATG', 'AUS', 'BTN', 'CAN', 'COG', 'CZE', 'GHA', 'GIN', 'HTI', 'ISL', 'JOR', 'KHM', 'KOR', 'LVA', 'MLT', 'MNE', 'MOZ', 'PER', 'SAH', 'SGP', 'SLV', 'SOM', 'TJK', 'TUV', 'UKR', 'WSM'],
                "source-layer": "country",
                "paint": {
                    "fill-color": "#292929"
                }
                // rainbow end
            }]
} as const;


export const THRUSTER_VALIDATION = {
    MAX_THRUST: {
        MIN: 0.001,
        MAX: 5,
        INPUT_LENGTH: 5,
        PARAMETER: 'N'
    },
    SPECIFIC_IMPULSE: {
        MIN: 40,
        MAX: 2500,
        INPUT_LENGTH: 4,
        PARAMETER: 'sec' // seconds
    },
    TOTAL_IMPULSE: {
        MIN: 10,
        MAX: 1e6, // 1×10⁶
        INPUT_LENGTH: 7,
        PARAMETER: 'Ns'
    },
    POWER_CONSUMPTION: {
        MIN: 1,
        MAX: 1500,
        INPUT_LENGTH: 4,
        PARAMETER: 'W' // Watts
    }
}

export const PROPULTION_VALIDATION = {
    STATION_KEEPING_TOLERANCE: {
        MIN: 0.01,
        MAX: 10,
        UNIT: 'km'
    },
    DRAG_COEFFICIENT: {
        MIN: 2.0,
        MAX: 2.5,
        UNIT: ''
    },
    EFFECTIVE_SURFACE_AREA: {
        MIN: 0.01,
        MAX: 3.0,
        UNIT: 'm²'
    },
    WET_MASS: {
        MIN: 1,
        MAX: 510,
        UNIT: 'kg'
    },
    MISSION_LIFE: {
        MIN: 0.1,
        MAX: 15,
        UNIT: 'yr',
    },
    SOLAR_RADIO_FLUX: {
        MIN: 70,
        MAX: 250,
        UNIT: 'sfu'
    },
    GEOMAGNETIC_INDEX: {
        MIN: 0,
        MAX: 100,
        UNIT: ''
    }
}

export const VIEW_TYPES = {
    UCP: 'UCP',
    SIMULATE: 'SIMULATE',
    OPERATE: 'OPERATE',
    MODELLING: 'MODELLING'
} as const;

export const VIEW_FEATURES = {
    [VIEW_TYPES.UCP]: {
        showLayersFilter: true,
        showEclipse: false,
        enableLighting: false,
        showGrid: false,
    },
    [VIEW_TYPES.SIMULATE]: {
        showLayersFilter: false,
        showEclipse: true,
        enableLighting: true,
        showGrid: false,         // ✅ grid disabled
    },
    [VIEW_TYPES.OPERATE]: {
        showLayersFilter: false,
        showEclipse: true,
        enableLighting: true,
        showGrid: true,
    },
    [VIEW_TYPES.MODELLING]: {
        showLayersFilter: false,
        showEclipse: true,
        enableLighting: true,
        showGrid: true,
    }
} as const;



export const Imaginary = new Cesium.MapboxStyleImageryProvider({
    styleId: 'dark-v10',
    accessToken: process.env.REACT_APP_MAPBOX_TOKEN!
});
export const InertiaZoomValue = 40000000;
export const MaximumZoomDistanceValue = 35500000;

export const MAX_SATELLITE__COUNT = 125
export const MAP_CENTER = [0, 0];

export const MAP_DEFAULT_ZOOM = 2.03;
export const MAP_MIN_ZOOM = 2.02;

export const OFFLINE_MAP_CONFIG = {
    style: WORLD_MAP_STYLE
};

export const FAULT_MARKER_THRESHOLD = 9;


export const ORBIT_FIELDS = [
    {
        label: 'Altitude',
        name: 'altitude',
        placeholder: 'Enter Altitude (km)',
        range: [VALIDATION_RULES?.ORBITAL_ALTITUDE?.MIN, VALIDATION_RULES?.ORBITAL_ALTITUDE?.MAX],
        max_length: VALIDATION_RULES?.ORBITAL_ALTITUDE.INPUT_LENGTH,
        parameter: 'km',
        unit: '(a)'
    }, {
        label: 'Eccentricity',
        name: 'eccentricity',
        placeholder: 'Enter Eccentricity (degree)',
        range: [VALIDATION_RULES.ORBITAL_ECCENTRICITY.MIN, VALIDATION_RULES.ORBITAL_ECCENTRICITY.MAX],
        max_length: VALIDATION_RULES?.ORBITAL_ECCENTRICITY.INPUT_LENGTH,
        parameter: '',
        unit: '(e)'
    }, {
        label: 'Inclination',
        name: 'inclination',
        placeholder: 'Enter Inclination (degree)',
        range: [VALIDATION_RULES.ORBITAL_INCLINATION.MIN, VALIDATION_RULES.ORBITAL_INCLINATION.MAX],
        max_length: VALIDATION_RULES?.ORBITAL_INCLINATION.INPUT_LENGTH,
        parameter: '°',
        unit: '(i)'
    }, {
        label: 'RAAN',
        name: 'RAAN',
        placeholder: 'Enter RAAN (degree)',
        range: [VALIDATION_RULES.ORBITAL_RAAN.MIN, VALIDATION_RULES.ORBITAL_RAAN.MAX],
        max_length: VALIDATION_RULES?.ORBITAL_RAAN.INPUT_LENGTH,
        parameter: '°',
        unit: '(Ω)'
    }, {
        label: 'Argument of Perigee',
        name: 'AP',
        placeholder: 'Enter Perigee (degree)',
        range: [VALIDATION_RULES.ORBITAL_AP.MIN, VALIDATION_RULES.ORBITAL_AP.MAX],
        max_length: VALIDATION_RULES?.ORBITAL_AP.INPUT_LENGTH,
        parameter: '°',
        unit: '(ω)'
    }, {
        label: 'True Anomaly',
        name: 'TA',
        placeholder: 'Enter Anomaly (degree)',
        range: [VALIDATION_RULES.ORBITAL_TA.MIN, VALIDATION_RULES.ORBITAL_TA.MAX],
        max_length: VALIDATION_RULES?.ORBITAL_TA.INPUT_LENGTH,
        parameter: '°',
        unit: '(ν)'
    }, {
        label: 'Phasing Angle',
        name: 'phasingAngle',
        placeholder: 'Enter Phasing Angle (degree)',
        range: [VALIDATION_RULES.ORBITAL_RAAN.MIN, VALIDATION_RULES.ORBITAL_RAAN.MAX],
        max_length: VALIDATION_RULES?.ORBITAL_RAAN.INPUT_LENGTH,
        parameter: '°',
        unit: 'φ'
    },
]

// YmlFileHandler constants
export const YML_EXTENSIONS = ['.yml', '.yaml'];
export const NAVIGATION_DELAY_SUCCESS = 1000;
export const NAVIGATION_DELAY_ERROR = 1500;
export const DEFAULT_FILENAME = 'file.yml';

/**
 * WebSocket event types for contact-related events
 * Used for O(1) lookup performance with Set.has()
 */
export const WS_CONTACT_EVENTS = new Set([
    'CONTACT_CREATED',
    'CONTACT_UPDATED',
    'CONTACT_DELETED'
] as const);

/**
 * WebSocket event type for validate subsystem health refreshes.
 * Kept separate from contact events so contact data is not refetched for health-only updates.
 */
export const WS_SUBSYSTEM_HEALTH_UPDATED_EVENT = 'SUBSYSTEM_HEALTH_UPDATED' as const;

/**
 * WebSocket event types for schedule-related events
 * Used for O(1) lookup performance with Set.has()
 */
export const WS_SCHEDULE_EVENTS = new Set([
    'ONBOARD_SCHEDULE_UPDATE'
] as const);

/**
 * WebSocket event types for groundstation enable disable related events
 */
export const WS_GROUNDSTATION_EVENTS = new Set([
    'GROUNDSTATION_ENABLED',
    'GROUNDSTATION_DISABLED'
] as const);

/**
 * WebSocket event fired when a validation test-case run is aborted
 * due to a critical command failure (e.g. ABORT_ON_FAILURE policy).
 */
export const WS_VALIDATE_ABORT_EVENT = 'VALIDATION_TEST_CASE_ABORTED' as const;

/**
 * SessionStorage key for the failure-handling mode chosen in the console Test form.
 * Possible values: 'ABORT_ON_FAILURE' | 'SKIP_AND_CONTINUE'
 */
export const SESSION_KEY_FAILURE_HANDLING = 'failureHandlingMode' as const;

/**
 * Custom window event name used to synchronise sessionStorage changes
 * across sibling React components (e.g. Test ↔ FormSubmit).
 */
export const CUSTOM_EVENT_STORAGE_UPDATE = 'storageUpdate' as const;

/**
 * Dual Editor / Console form constants
 * Used by DualEditorForm for telecommand JSON editing
 */
export const CONSOLE_READ_ONLY_PATHS = [
    "sequenceData.commandId",
    "commandName",
    "commandType",
] as const;

export const CONSOLE_READ_ONLY_PATHS_SET = new Set(CONSOLE_READ_ONLY_PATHS);

export const CONSOLE_ANGLE_FIELDS = [
    "sequenceData.commandData.RollAngle",
    "sequenceData.commandData.PitchAngle",
    "sequenceData.commandData.YawAngle",
] as const;

export const CONSOLE_EMPTY_ARRAY_MARKER = "__EMPTY_ARRAY__";

export const CONSOLE_REGEX = {
    /** Negative and positive integers, no decimals */
    INTEGER_INPUT: /^-?\d*$/,
    /** Matches array index like [0], [1] */
    ARRAY_INDEX: /\[\d+\]/g,
    /** Splits path for flatten/unflatten (e.g. "a.b[0]" -> ["a","b","0"]) */
    PATH_SPLIT: /\.|\[|\]/,
    /** Extracts label from key (e.g. "arr[0]" from "a.b.arr[0]") */
    LABEL_FROM_KEY: /([^.[]+\[\d+\])$/,
    /** Leading zeros in numeric input (e.g. "0123") */
    LEADING_ZEROS: /^0\d+$/,
    /** Leading zeros to strip from display value */
    LEADING_ZEROS_REPLACE: /^0+(?=\d)/,
} as const;

export const CONSOLE_ANGLE_RANGE = { MIN: -180, MAX: 180 } as const;

export const CONSOLE_ERROR_MESSAGES = {
    STRUCTURE_MODIFICATION: "Structure modification not allowed",
    CANNOT_MODIFY_KEYS: "Cannot add, remove, or modify keys",
    INVALID_JSON: "Invalid JSON",
} as const;

export const CONSOLE_DEFAULT_EXPANDED_PATHS = {
    SEQUENCE_DATA: "sequenceData",
    COMMAND_DATA: "sequenceData.commandData",
    COMMAND_HEADER: "sequenceData.commandHeader",
} as const;

export const VALID_JSON_EVENT = "validJSONEvent";

export const JSON_VALIDATE_DEBOUNCE_MS = 400;

/** Indentation spaces for JSON.stringify */
export const JSON_INDENT_SPACES = 2;

/** Empty JSON string for cleared editor state */
export const EMPTY_JSON_STR = "{}";

export const SENSOR_CONFIG_TYPES = [
    {
        label: 'Conic',
        value: 'conic',

    },
    {
        label: 'Rectangular',
        value: 'rectangular',

    }
]
